import camelcaseKeys from 'utils/camelCaseKeys';
import { catchNetworkExceptions, DISABLE_ERROR_HANDLER } from 'utils/exception';
import { errorResponseMatcher } from 'utils/exception/mapNetworkStatuses';

import { Codeowners } from 'components/Common/Error/Codeowners';
import RestAPI from 'components/Common/restApi';
import { OnlineBookingErrors } from 'components/OnlineBooking/api/errors';

import { State } from '../state/types';

import { countryShortcut } from './countryShortcut';
import { formatCAZipCode } from './formatCAZipCode';

interface OrganizationInfo {
  logo_url: string;
  object: string;
  phone_number: string;
  printable_address: string;
  thumb_logo_url: string;
  time_zone: string;
  url: string;
  website: string;
  founding_pro_uuid: string;
  default_arrival_window: number;
  id: string;
  email: string;
}

export type TimeSlot = {
  from: string;
  to: string;
  type: 'exact' | 'range';
};

export type TimeSlotDict = {
  [k: string]: TimeSlot[];
};

export type Availability = {
  date: string;
  time_slots: TimeSlot[];
};

export type ConfirmBookingPayload = Omit<
  State,
  'step' | 'previousStep' | 'success' | 'industry' | 'category' | 'unavailableDateError'
>;

export const fetchServices = (uuid: string) => {
  return catchNetworkExceptions(
    () =>
      RestAPI.getWithFormData('/online_booking/available_services', {
        organization_uuid: uuid,
      }).then((response: any) => ({
        ...response,
        ...(response?.data ? { data: camelcaseKeys(response.data) } : {}),
      })),
    Codeowners.CUSTOMER_INTAKE,
    DISABLE_ERROR_HANDLER
  );
};

export const fetchTimeSlots = (uuid: string, dateFrom: string, dateTo: string, serviceUuid: string) => {
  return catchNetworkExceptions(
    () =>
      RestAPI.getWithQueryString<
        Availability[],
        { organization_uuid: string; date_from: string; date_to: string; service_uuid: string }
      >(`/online_booking/available_time_slots`, {
        organization_uuid: uuid,
        date_from: dateFrom,
        date_to: dateTo,
        service_uuid: serviceUuid,
      }),
    Codeowners.CUSTOMER_INTAKE,
    DISABLE_ERROR_HANDLER
  );
};

export const fetchOrgData = (uuid: string) => {
  return catchNetworkExceptions(
    () => RestAPI.get<OrganizationInfo>(`/alpha/organizations/${uuid}`),
    Codeowners.CUSTOMER_INTAKE,
    errorResponseMatcher({
      [OnlineBookingErrors.orgNotFound]: {
        status: 404,
        path: 'error.message',
        value: OnlineBookingErrors.orgNotFound,
      },
    })
  );
};

export const confirmBooking = ({
  service,
  description,
  orgUuid,
  contactDetails: { values },
  arrivalWindow: { date, time },
  question1,
  question2,
}: ConfirmBookingPayload) => {
  const isCanada = values.country === 'Canada';
  return catchNetworkExceptions(
    () =>
      RestAPI.postWithFormData('/online_booking/confirm_booking', {
        service_uuid: service?.uuid,
        private_notes: `${description}${question1}${question2}`,
        organization_uuid: orgUuid,
        contact_details: {
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phone,
          email: values.email,
          state: values.state,
          city: values.city,
          zip_code: isCanada ? formatCAZipCode(values.zipCode) : values.zipCode,
          street_address: values.addressLine1,
          unit: values.addressLine2,
          country: countryShortcut(values.country),
        },
        arrival_window: {
          date,
          start_time: time?.from,
        },
      }),
    Codeowners.CUSTOMER_INTAKE,
    DISABLE_ERROR_HANDLER
  );
};
